/* Native Dawn owns variant requests, inventory and the cart drawer. */
customElements.define('sansa-whatsapp-float', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.resizeObserver = new ResizeObserver(() => this.measureCart());
    this.cartObserver = new MutationObserver(() => this.measureCart());
    const options = { signal: this.abort.signal };
    window.addEventListener('resize', () => this.measureCart(), options);
    document.addEventListener('scroll', () => this.measureControls(), { ...options, capture: true, passive: true });
    document.addEventListener('shopify:section:load', () => this.watchCart(), options);
    document.addEventListener('product-info:loaded', () => this.watchCart(), options);
    this.watchCart();
  }
  watchCart() {
    this.resizeObserver.disconnect();
    this.cartObserver.disconnect();
    this.cart = document.querySelector('sansa-sticky-cart');
    if (this.cart) {
      this.resizeObserver.observe(this.cart);
      this.cartObserver.observe(this.cart, { attributes: true, attributeFilter: ['hidden'] });
    }
    this.measureCart();
  }
  measureCart() {
    const visible = this.cart && !this.cart.hidden && getComputedStyle(this.cart).display !== 'none';
    const height = visible ? Math.ceil(this.cart.getBoundingClientRect().height) : 0;
    this.style.setProperty('--sansa-whatsapp-cart-offset', `${height}px`);
    this.measureControls();
  }
  measureControls() {
    const button = this.getBoundingClientRect();
    const controls = document.querySelectorAll('product-info [data-guide-open],product-info [data-sansa-size-group],product-info .sansa-buy-controls,product-info [data-sansa-buy-now],.sansa-newsletter__field');
    const overlaps = Array.from(controls).some(control => {
      const rect = control.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.left < button.right && rect.right > button.left && rect.top < button.bottom && rect.bottom > button.top;
    });
    this.toggleAttribute('data-control-overlap', overlaps);
  }
  disconnectedCallback() { this.abort?.abort(); this.resizeObserver?.disconnect(); this.cartObserver?.disconnect(); }
});
customElements.define('sansa-fullscreen-hero', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.observer = new ResizeObserver(() => this.measureHeader());
    this.watchHeaders();
    document.addEventListener('shopify:section:load', () => this.watchHeaders(), { signal: this.abort.signal });
  }
  watchHeaders() {
    this.observer.disconnect();
    this.headers = Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    this.headers.forEach(header => this.observer.observe(header));
    this.measureHeader();
  }
  measureHeader() {
    const height = this.headers.reduce((total, header) => total + header.getBoundingClientRect().height, 0);
    this.style.setProperty('--sansa-header-stack-height', `${height}px`);
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
});
document.addEventListener('click', event => {
  const link = event.target.closest('header-drawer a[href]');
  if (!link) return;
  const destination = new URL(link.href, location.href);
  if (destination.origin === location.origin && destination.pathname === location.pathname && destination.hash) link.closest('header-drawer').querySelector('details[open] > summary')?.click();
});
customElements.define('sansa-size-guide', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    const dialog = this.querySelector('dialog');
    const opener = this.querySelector('[data-guide-open]');
    opener.addEventListener('click', () => dialog.showModal(), options);
    this.querySelector('[data-guide-close]').addEventListener('click', () => dialog.close(), options);
    dialog.addEventListener('close', () => opener.focus({ preventScroll: true }), options);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
    }, options);
  }
  disconnectedCallback() { this.abort?.abort(); }
});
function initializeSansaProduct(product) {
  if (!product.matches('[data-sansa-product]') || product.sansaControls) return;
  const state = product.sansaControls = {
    chosen: product.dataset.sizeChosen === 'true' || !product.querySelector('[data-sansa-size]'),
    busy: false,
    variant: JSON.parse(product.querySelector('[data-selected-variant], [data-sansa-initial-variant]')?.textContent || 'null'),
  };
  const sizeGroup = () => product.querySelector('[data-sansa-size-group]');
  const atc = () => product.querySelector('[id^="ProductSubmitButton-"]');
  const buy = () => product.querySelector('[data-sansa-buy-now]');
  const form = () => product.querySelector('product-form form');
  const error = message => product.querySelector('product-form')?.handleErrorMessage(message);
  state.choose = () => {
    const group = sizeGroup();
    group?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const target = group?.querySelector('[data-sansa-size]:checked:not(:disabled)') || group?.querySelector('[data-sansa-size]:not(:disabled)') || group;
    target?.focus({ preventScroll: true });
  };
  state.sync = () => {
    const group = sizeGroup();
    if (group && !state.busy) {
      const desired = state.variant?.options[Number(group.dataset.optionPosition) - 1];
      const choices = Array.from(group.querySelectorAll('[data-sansa-size]'));
      const option = choices.find(item => item.value === desired);
      if (state.chosen && (!option || option.disabled)) state.chosen = false;
      choices.forEach(item => {
        item.checked = state.chosen && item.value === desired;
        item.toggleAttribute('checked', item.checked);
      });
    }
    const available = Boolean(state.variant?.available);
    const blocked = state.busy || !state.chosen || !available;
    if (atc()) {
      atc().disabled = blocked;
      atc().querySelector('span').textContent = state.busy ? 'Updating…' : !state.chosen ? 'Choose a size' : available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
    }
    if (buy()) buy().disabled = blocked;
    product.dataset.sizeChosen = String(state.chosen);
    product.dispatchEvent(new CustomEvent('sansa:controls-updated'));
  };
  product.addEventListener('change', event => {
    if (event.target.matches('[data-sansa-size]')) state.chosen = event.target.checked;
  }, true);
  product.addEventListener('sansa:variant-loading', () => { state.busy = true; state.sync(); });
  product.addEventListener('sansa:variant-ready', event => {
    state.variant = event.detail.variant; state.busy = false;
    if (!state.variant) state.chosen = false;
    state.sync();
  });
  product.addEventListener('sansa:variant-error', () => {
    state.busy = false; state.variant = null; state.chosen = false; state.sync();
    error('We couldn’t update this selection. Please choose your colour and size again.');
  });
  product.addEventListener('submit', event => {
    if (event.target !== form()) return;
    if (!state.chosen || state.busy || !state.variant?.available) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!state.chosen) state.choose();
    } else if (!form().reportValidity()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  product.addEventListener('click', async event => {
    const button = event.target.closest('[data-sansa-buy-now]');
    if (!button || button.disabled || state.busy || !state.chosen || !state.variant?.available || !form().reportValidity()) return;
    state.busy = true; state.sync(); error(); button.textContent = 'Taking you to checkout…';
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form()),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'This size could not be added. Please try again.');
      location.assign(`${window.Shopify.routes.root}checkout`);
    } catch (failure) {
      state.busy = false; state.sync(); button.textContent = 'Buy now';
      error(failure.message || 'We couldn’t open checkout. Please try again.');
    }
  });
  const buttonObserver = new MutationObserver(() => {
    if (buy()) buy().disabled = state.busy || !state.chosen || !state.variant?.available || atc().disabled || atc().getAttribute('aria-disabled') === 'true';
  });
  if (atc()) buttonObserver.observe(atc(), { attributes: true, attributeFilter: ['disabled','aria-disabled'] });
  state.sync();
}
document.addEventListener('product-info:loaded', event => initializeSansaProduct(event.target));
document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-sansa-product]').forEach(initializeSansaProduct));
window.addEventListener('pageshow', event => {
  // A checkout return must refresh stock and cart state, including a pending Buy now.
  if (event.persisted) location.reload();
});
customElements.define('sansa-sticky-cart', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const product = this.closest('product-info');
    const source = product.querySelector('[id^="ProductSubmitButton-"]');
    const purchase = product.querySelector('.sansa-buy-controls');
    const price = product.querySelector('[id^="price-"]');
    const button = this.querySelector('button');
    if (!source || !purchase) return;
    let purchaseVisible = true;
    const sync = () => {
      const state = product.sansaControls;
      this.hidden = purchaseVisible || Boolean(document.querySelector('dialog[open],cart-drawer.active,product-modal[open],.menu-drawer-container[open]'));
      button.disabled = Boolean(state?.busy) || (Boolean(state?.chosen) && (source.disabled || source.getAttribute('aria-disabled') === 'true'));
      button.textContent = !state?.chosen ? 'Choose size' : source.querySelector('span').textContent.trim();
      const selectedOptions = state?.variant?.options.map((value, index) => {
        const group = product.querySelector(`[data-sansa-size-group][data-option-position="${index + 1}"]`);
        return group?.querySelector('[data-sansa-size]:checked')?.dataset.sizeLabel || value;
      }).join(' / ');
      this.querySelector('[data-sticky-options]').textContent = state?.chosen ? selectedOptions || '' : 'Find her perfect fit';
      const currentPrice = price.querySelector('.price--on-sale .price-item--sale') || price.querySelector('.price__regular .price-item');
      this.querySelector('[data-sticky-price]').textContent = price.classList.contains('hidden') ? '' : currentPrice?.textContent.trim() || '';
    };
    button.addEventListener('click', () => { if (!product.sansaControls?.chosen) product.sansaControls?.choose(); else source.click(); }, { signal: this.abort.signal });
    product.addEventListener('sansa:controls-updated', sync, { signal: this.abort.signal });
    this.visibility = new IntersectionObserver(([entry]) => { purchaseVisible = entry.isIntersecting; sync(); });
    this.visibility.observe(purchase);
    this.observer = new MutationObserver(mutations => { if (mutations.some(item => !this.contains(item.target))) sync(); });
    this.observer.observe(product, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled','aria-disabled','class','open'] });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    sync();
  }
  disconnectedCallback() { this.abort?.abort(); this.visibility?.disconnect(); this.observer?.disconnect(); }
});
customElements.define('sansa-gallery-dots', class extends HTMLElement {
  connectedCallback() {
    const gallery = this.closest('media-gallery');
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const list = viewer.querySelector('ul');
    this.abort = new AbortController();
    const update = () => {
      const items = Array.from(list.querySelectorAll('li[data-media-id]'));
      if (items.length < 2) { this.hidden = true; return; }
      const current = viewer.querySelector('.slider-counter--current')?.textContent || '1';
      const fragment = document.createDocumentFragment();
      items.forEach((item, index) => {
        const button = document.createElement('button'); button.type = 'button';
        button.setAttribute('aria-label', `Show photograph ${index + 1}`);
        button.setAttribute('aria-current', String(index + 1 === Number(current)));
        button.dataset.media = item.dataset.mediaId; fragment.append(button);
      });
      this.replaceChildren(fragment);
    };
    this.addEventListener('click', event => { const button = event.target.closest('button'); if (button) gallery.setActiveMedia(button.dataset.media, false); }, { signal: this.abort.signal });
    viewer.addEventListener('slideChanged', update, { signal: this.abort.signal });
    this.observer = new MutationObserver(update); this.observer.observe(list, { childList: true }); update();
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
});
customElements.define('sansa-new-arrivals', class extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    const url = new URL(this.dataset.collection, location.origin);
    if (this.dataset.newest === 'true') url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('section_id', this.dataset.section);
    this.abort = new AbortController();
    try {
      const response = await fetch(url, { signal: this.abort.signal });
      if (!response.ok) throw new Error('Collection unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = html.querySelector('.sansa-product-grid');
      if (!grid?.children.length) return;
      this.querySelector('.sansa-product-grid').replaceChildren(...grid.children); this.dataset.loaded = 'true';
    } catch (error) { if (error.name !== 'AbortError') this.dataset.loaded = 'fallback'; }
  }
  disconnectedCallback() { this.abort?.abort(); }
});
